import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';

/**
 * DisclaimerPage - Important Disclaimer page
 * Migrated from public/disclaimer.html with all original content preserved
 */
export function DisclaimerPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      <Navbar />

      {/* Header */}
      <section className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">⚠️ Important Disclaimer</h1>
            <p className="text-xl text-red-200">Please read carefully before using FinModel</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* Critical Warning */}
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-8 mb-8">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-3xl font-bold text-red-800 mb-4">CRITICAL WARNING</h2>
                <p className="text-xl text-red-700 font-bold">
                  FREE EDUCATIONAL TOOL - USE AT YOUR OWN RISK
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded">
                  <h3 className="font-bold text-red-800 mb-2">🎓 Educational Purpose Only</h3>
                  <p className="text-red-700 text-sm">
                    FinModel is designed for educational purposes only. It is NOT intended to provide
                    professional financial, investment, or business advice.
                  </p>
                </div>

                <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded">
                  <h3 className="font-bold text-red-800 mb-2">✋ Your Responsibility</h3>
                  <p className="text-red-700 text-sm">
                    YOU are solely responsible for verifying all calculations, assumptions, and outputs. Do
                    not rely solely on these models for business decisions.
                  </p>
                </div>

                <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded">
                  <h3 className="font-bold text-red-800 mb-2">🔍 No Warranty</h3>
                  <p className="text-red-700 text-sm">
                    This tool is provided "AS IS" without any warranties. We make no guarantees about
                    accuracy, completeness, or suitability for any purpose.
                  </p>
                </div>

                <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded">
                  <h3 className="font-bold text-red-800 mb-2">💼 Consult Professionals</h3>
                  <p className="text-red-700 text-sm">
                    ALWAYS consult qualified accountants, financial advisors, and business professionals
                    before making any decisions.
                  </p>
                </div>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">What FinModel Is</h2>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-600">
                <li>A free educational tool for learning financial modeling concepts</li>
                <li>A starting point for understanding business metrics and projections</li>
                <li>A template generator for common business model structures</li>
                <li>A way to explore "what-if" scenarios for educational purposes</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">What FinModel Is NOT</h2>
              <ul className="list-disc pl-6 mb-6 space-y-2 text-slate-600">
                <li>Professional financial advice or consulting</li>
                <li>A substitute for qualified accountants or financial advisors</li>
                <li>A guarantee of business success or viability</li>
                <li>Suitable for making investment or lending decisions</li>
                <li>Compliant with specific accounting standards without verification</li>
                <li>A replacement for professional due diligence</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">Limitations and Risks</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-amber-800 mb-3">⚠️ Key Limitations</h3>
                <ul className="list-disc pl-6 space-y-2 text-amber-700">
                  <li><strong>Simplified Models:</strong> Real businesses are more complex than these models</li>
                  <li><strong>Generic Assumptions:</strong> Industry-specific factors may not be covered</li>
                  <li><strong>No Validation:</strong> Calculations are not verified by professionals</li>
                  <li><strong>Market Conditions:</strong> Models don't account for changing market conditions</li>
                  <li><strong>Regulatory Compliance:</strong> May not meet specific regulatory requirements</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">Required Actions Before Use</h2>
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-primary-800 mb-3">✅ Before Using FinModel</h3>
                <ol className="list-decimal pl-6 space-y-2 text-primary-700">
                  <li>Understand that this is an educational tool only</li>
                  <li>Acknowledge that you use it at your own risk</li>
                  <li>Commit to verifying all outputs with professionals</li>
                  <li>Agree not to rely solely on these models for decisions</li>
                  <li>Read and understand all terms and conditions</li>
                </ol>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">Specific Business Disclaimers</h2>
              <div className="space-y-4 mb-6">
                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800">💻 SaaS Models</h4>
                  <p className="text-sm text-slate-600">
                    Actual SaaS metrics vary significantly by market, product, and implementation.
                    Churn, LTV, and CAC calculations are simplified.
                  </p>
                </div>

                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800">🍽️ Restaurant Models</h4>
                  <p className="text-sm text-slate-600">
                    Food service businesses have complex operational factors, local regulations,
                    and seasonal variations not captured in these models.
                  </p>
                </div>

                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800">🛍️ E-commerce Models</h4>
                  <p className="text-sm text-slate-600">
                    Online retail involves complex logistics, marketing costs, and platform
                    dependencies that require professional analysis.
                  </p>
                </div>

                <div className="border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-800">🏭 Manufacturing Models</h4>
                  <p className="text-sm text-slate-600">
                    Production businesses have complex supply chain, inventory, and operational
                    considerations requiring expert evaluation.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">No Liability</h2>
              <p className="mb-6 text-slate-600">
                By using FinModel, you acknowledge that the creators, contributors, and hosting providers bear
                no responsibility for any losses, damages, or consequences arising from your use of this tool.
                This includes but is not limited to business failures, investment losses, or incorrect business
                decisions.
              </p>

              <h2 className="text-2xl font-bold text-slate-900 mb-4">Professional Requirements</h2>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-orange-800 mb-3">👨‍💼 Required Professional Consultations</h3>
                <ul className="list-disc pl-6 space-y-2 text-orange-700">
                  <li><strong>Certified Public Accountant (CPA):</strong> For accounting standards and financial statement preparation</li>
                  <li><strong>Financial Advisor:</strong> For investment and funding strategies</li>
                  <li><strong>Business Attorney:</strong> For legal structure and compliance</li>
                  <li><strong>Industry Expert:</strong> For sector-specific considerations</li>
                  <li><strong>Tax Professional:</strong> For tax implications and strategies</li>
                </ul>
              </div>
            </div>

            {/* Final Warning */}
            <div className="mt-8 pt-8 border-t border-slate-200">
              <div className="bg-red-800 text-white rounded-lg p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">FINAL WARNING</h3>
                <p className="text-lg font-semibold mb-4">
                  By using FinModel, you acknowledge that you understand this is an educational tool only and
                  that you accept full responsibility for validating all outputs with qualified professionals
                  before making any business or financial decisions.
                </p>
                <p className="text-red-200">
                  If you do not agree to these terms, please do not use this service.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
